import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import { Trash2, Edit2, Loader2, Mail, User, Lock, Search, X, Eye, EyeOff, Hash, Building2, ChevronDown, Check, Phone } from 'lucide-react';
import { useReactTable, getCoreRowModel, flexRender, createColumnHelper } from '@tanstack/react-table';

type RegistrationStatus = 'REGISTERED' | 'UNREGISTERED';
type RegistrationFilter = 'ALL' | RegistrationStatus;

type UserRow = {
  id: string;
  name?: string;
  email?: string;
  role?: string | null;
  department?: string;
  designation?: string;
  mpId?: string;
  mobile?: string;
  registrationStatus?: RegistrationStatus;
};

const columnHelper = createColumnHelper<UserRow>();

export function UserMaster() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingRegistrationStatus, setEditingRegistrationStatus] = useState<RegistrationStatus>('REGISTERED');
  const [form, setForm] = useState({
    name: '',
    email: '',
    mpId: '',
    mobile: '',
    department: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const [searchFilter, setSearchFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [registrationFilter, setRegistrationFilter] = useState<RegistrationFilter>('ALL');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchBoxRef = useRef<HTMLDivElement>(null);

  const [showDeptDropdown, setShowDeptDropdown] = useState(false);
  const [deptSearchQuery, setDeptSearchQuery] = useState('');
  const deptDropdownRef = useRef<HTMLDivElement>(null);

  const isEditingUnregistered = editingRegistrationStatus === 'UNREGISTERED';

  const { data: distinctDepartmentsData } = useQuery({
    queryKey: ['departments-distinct'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/departments/distinct');
      return res.data.data as string[];
    },
  });
  const distinctDepartments = distinctDepartmentsData || [];

  const filteredDepartments = useMemo(() => {
    if (!deptSearchQuery.trim()) return distinctDepartments;
    return distinctDepartments.filter((d) =>
      d.toLowerCase().includes(deptSearchQuery.trim().toLowerCase())
    );
  }, [distinctDepartments, deptSearchQuery]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchFilter.trim()), 300);
    return () => clearTimeout(t);
  }, [searchFilter]);

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setShowSuggestions((open) => (open ? false : open));
      }
      if (deptDropdownRef.current && !deptDropdownRef.current.contains(e.target as Node)) {
        setShowDeptDropdown(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  const { data: usersData, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['adminUsers', debouncedSearch, registrationFilter],
    queryFn: async ({ pageParam = undefined }) => {
      const res = await apiClient.get('/admin/users', {
        params: {
          cursor: pageParam,
          registrationStatus: registrationFilter,
          ...(debouncedSearch ? { search: debouncedSearch, searchBy: 'all' } : {}),
        },
      });
      return res.data.data;
    },
    getNextPageParam: (lastPage) => (lastPage.hasNextPage ? lastPage.endCursor : undefined),
    initialPageParam: undefined as string | undefined,
  });

  const flatUsers = useMemo(
    () => usersData?.pages.flatMap((page: { items: UserRow[] }) => page.items) ?? [],
    [usersData]
  );
  const suggestions = debouncedSearch && showSuggestions ? flatUsers.slice(0, 8) : [];

  const createMutation = useMutation({
    mutationFn: async (userData: typeof form) => {
      const res = await apiClient.post('/admin/users', userData);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      setIsModalOpen(false);
      setForm({ name: '', email: '', mpId: '', mobile: '', department: '', password: '' });
      setDeptSearchQuery('');
      setShowDeptDropdown(false);
      setShowPassword(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
      registrationStatus,
    }: {
      id: string;
      data: typeof form;
      registrationStatus: RegistrationStatus;
    }) => {
      const payload =
        registrationStatus === 'UNREGISTERED'
          ? { name: data.name, email: data.email, mpId: data.mpId, mobile: data.mobile, department: data.department }
          : data;
      const res = await apiClient.put(`/admin/users/${encodeURIComponent(id)}`, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      setIsModalOpen(false);
      setEditingUserId(null);
      setEditingRegistrationStatus('REGISTERED');
      setForm({ name: '', email: '', mpId: '', mobile: '', department: '', password: '' });
      setDeptSearchQuery('');
      setShowDeptDropdown(false);
      setShowPassword(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete(`/admin/users/${id}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
    },
  });

  const deleteUser = deleteMutation.mutate;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingUserId) {
      updateMutation.mutate({
        id: editingUserId,
        data: form,
        registrationStatus: editingRegistrationStatus,
      });
    } else {
      createMutation.mutate(form);
    }
  };

  const openEditModal = useCallback((user: UserRow) => {
    const dept = user.department || '';
    setEditingUserId(user.id);
    setEditingRegistrationStatus(user.registrationStatus === 'UNREGISTERED' ? 'UNREGISTERED' : 'REGISTERED');
    setForm({
      name: user.name || '',
      email: user.email || '',
      mpId: user.mpId || '',
      mobile: user.mobile || '',
      department: dept,
      password: '', // cannot show real password — stored as hash / N/A for unregistered
    });
    setDeptSearchQuery(dept);
    setShowDeptDropdown(false);
    setShowPassword(false);
    setIsModalOpen(true);
  }, []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', { header: 'Name' }),
      columnHelper.accessor('email', { header: 'Email' }),
      columnHelper.accessor('role', {
        header: 'Role',
        cell: (info) => {
          const role = info.getValue();
          if (!role) {
            return <span className="text-xs text-slate-400">—</span>;
          }
          return (
            <span
              className={`inline-flex items-center whitespace-nowrap px-2.5 py-1 rounded-full text-xs font-medium leading-none ${role === 'ADMIN' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                }`}
            >
              {String(role).replaceAll('_', ' ')}
            </span>
          );
        },
      }),
      columnHelper.accessor('department', { header: 'Department' }),
      columnHelper.accessor('registrationStatus', {
        header: 'Status',
        cell: (info) => {
          const status = info.getValue() || 'REGISTERED';
          const isRegistered = status === 'REGISTERED';
          return (
            <span
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide ${isRegistered
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-amber-50 text-amber-800 border border-amber-200'
                }`}
            >
              {isRegistered ? 'Registered' : 'Unregistered'}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const row = info.row.original;
          const isUnregistered = row.registrationStatus === 'UNREGISTERED';
          const isDeletable = !isUnregistered && row.role === 'APPROVAL_TEAM';

          return (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => openEditModal(row)}
                className="p-1.5 rounded-md transition-colors text-blue-500 hover:bg-blue-50 cursor-pointer"
                title={isUnregistered ? 'Edit Employee' : 'Edit User'}
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  if (isDeletable && confirm('Are you sure you want to delete this user?')) {
                    deleteUser(row.id);
                  }
                }}
                disabled={!isDeletable}
                className={`p-1.5 rounded-md transition-colors ${isDeletable ? 'text-red-500 hover:bg-red-50 cursor-pointer' : 'text-slate-300 cursor-not-allowed'
                  }`}
                title={
                  isUnregistered
                    ? 'Unregistered employees cannot be deleted here'
                    : isDeletable
                      ? 'Delete User'
                      : 'Cannot delete this user'
                }
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        },
      }),
    ],
    [openEditModal, deleteUser]
  );

  const table = useReactTable({
    data: flatUsers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Layout title="User Master">
      <div className="mb-2 flex flex-col sm:flex-row sm:items-center gap-2" ref={searchBoxRef}>
        <div className="relative w-full max-w-sm">
          <div
            className={`group flex items-center gap-2 rounded-xl border bg-white px-3 py-2 shadow-sm transition-all ${showSuggestions && debouncedSearch
              ? 'border-blue-400 ring-2 ring-[#1C4ED8]/15 shadow-md shadow-blue-500/5'
              : 'border-slate-200 hover:border-slate-300 hover:shadow'
              }`}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 group-focus-within:bg-blue-50 group-focus-within:text-[#1C4ED8] transition-colors">
              <Search className="w-3.5 h-3.5" />
            </span>
            <input
              type="text"
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              placeholder="Search by name, email or employee ID"
              className="min-w-0 flex-1 bg-transparent text-sm text-slate-800 placeholder:text-slate-400 outline-none"
            />
            {searchFilter ? (
              <button
                type="button"
                onClick={() => {
                  setSearchFilter('');
                  setShowSuggestions(false);
                }}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                title="Clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            ) : null}
          </div>

          {showSuggestions && debouncedSearch && (
            <div className="absolute z-50 left-0 right-0 mt-1.5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg shadow-slate-200/80 max-h-[280px] overflow-y-auto">
              {isLoading ? (
                <div className="px-3.5 py-3 text-xs text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[#1C4ED8]" /> Searching...
                </div>
              ) : suggestions.length === 0 ? (
                <div className="px-3.5 py-3 text-xs text-slate-500">No users found</div>
              ) : (
                <div className="p-1">
                  {suggestions.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => {
                        setSearchFilter(user.email || user.name || '');
                        setShowSuggestions(false);
                      }}
                      className="w-full text-left px-3 py-2.5 rounded-lg cursor-pointer transition-colors flex items-start gap-3 hover:bg-slate-50 border border-transparent hover:border-slate-100"
                    >
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-50 to-slate-100 text-[11px] font-bold text-[#1C4ED8]">
                        {(user.name || '?').charAt(0).toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1 flex flex-col">
                        <span className="text-sm font-semibold text-slate-800 truncate">{user.name || '—'}</span>
                        <span className="text-xs text-slate-500 truncate">{user.email}</span>
                        <span className="text-[10px] font-medium text-slate-400 mt-0.5 truncate">
                          {[
                            user.registrationStatus === 'UNREGISTERED' ? 'Unregistered' : 'Registered',
                            user.mpId,
                            user.department,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {(
            [
              { value: 'ALL', label: 'All' },
              { value: 'REGISTERED', label: 'Registered' },
              { value: 'UNREGISTERED', label: 'Unregistered' },
            ] as const
          ).map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setRegistrationFilter(opt.value)}
              className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer ${registrationFilter === opt.value
                ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-slate-200 bg-slate-50">
                  {headerGroup.headers.map((header) => (
                    <th key={header.id} className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-12 text-center text-slate-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading users...
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-12 text-center text-slate-500">
                    No users found.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50/50 transition-colors">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-6 py-4 text-sm text-slate-700">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {hasNextPage && (
          <div className="p-4 border-t border-slate-200 flex justify-center">
            <button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg flex items-center transition-colors disabled:opacity-50"
            >
              {isFetchingNextPage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {isFetchingNextPage ? 'Loading...' : 'Load More Users'}
            </button>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md relative">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center rounded-t-xl bg-white">
              <div>
                <h3 className="font-bold text-slate-800">
                  {editingUserId
                    ? isEditingUnregistered
                      ? 'Edit Employee'
                      : 'Edit User'
                    : 'Create User'}
                </h3>
                {editingUserId && (
                  <p className="text-[11px] font-semibold text-slate-500 mt-0.5">
                    {isEditingUnregistered ? 'Unregistered (Employee registry)' : 'Registered (Portal user)'}
                  </p>
                )}
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    required
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="John Doe"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Employee ID</label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={form.mpId}
                    onChange={(e) => setForm({ ...form, mpId: e.target.value })}
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Employee ID"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Mobile Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="tel"
                    value={form.mobile}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/[^0-9+]/g, '').slice(0, 15);
                      setForm({ ...form, mobile: numericValue });
                    }}
                    className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder="Mobile Number (numbers only)"
                  />
                </div>
              </div>

              <div ref={deptDropdownRef} className="relative">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Department</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search and select department..."
                    value={deptSearchQuery}
                    onChange={(e) => {
                      const val = e.target.value;
                      setDeptSearchQuery(val);
                      setForm((prev) => ({ ...prev, department: val }));
                      setShowDeptDropdown(true);
                    }}
                    onFocus={() => setShowDeptDropdown(true)}
                    className="w-full pl-9 pr-9 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-800 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                    <ChevronDown className="w-4 h-4" />
                  </span>

                  {showDeptDropdown && (
                    <div className="absolute z-50 left-0 right-0 bottom-full mb-1.5 bg-white border border-slate-200 rounded-xl shadow-2xl max-h-48 overflow-y-auto divide-y divide-slate-100 animate-in fade-in slide-in-from-bottom-1 duration-150">
                      {filteredDepartments.length === 0 ? (
                        <div className="px-3.5 py-2.5 text-xs text-slate-400 italic text-center">
                          {deptSearchQuery ? `Press enter or keep typing for "${deptSearchQuery}"` : 'No departments found'}
                        </div>
                      ) : (
                        filteredDepartments.map((dept) => (
                          <div
                            key={dept}
                            onClick={() => {
                              setForm((prev) => ({ ...prev, department: dept }));
                              setDeptSearchQuery(dept);
                              setShowDeptDropdown(false);
                            }}
                            className={`px-3.5 py-2.5 transition-colors cursor-pointer text-xs font-medium flex items-center justify-between ${form.department === dept
                              ? 'bg-blue-50 text-blue-700 font-semibold'
                              : 'text-slate-700 hover:bg-slate-50'
                              }`}
                          >
                            <span>{dept}</span>
                            {form.department === dept && <Check className="w-3.5 h-3.5 text-blue-600" />}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  Password{' '}
                  {isEditingUnregistered
                    ? '(Not available for unregistered)'
                    : editingUserId
                      ? '(Leave blank to keep current)'
                      : ''}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required={!editingUserId}
                    disabled={isEditingUnregistered}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className={`w-full pl-9 pr-10 py-2 border border-slate-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none ${isEditingUnregistered ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''
                      }`}
                    placeholder={
                      isEditingUnregistered
                        ? 'No password — not a portal login'
                        : editingUserId
                          ? 'Enter new password to reset'
                          : 'Password'
                    }
                  />
                  {!isEditingUnregistered && (
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:text-slate-600"
                      title={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>

              {(createMutation.isError || updateMutation.isError) && (
                <p className="text-red-500 text-xs mt-2">
                  {((createMutation.error || updateMutation.error) as any).response?.data?.error?.message ||
                    'Failed to process request'}
                </p>
              )}

              <div className="pt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center"
                >
                  {createMutation.isPending || updateMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  {editingUserId ? (isEditingUnregistered ? 'Update Employee' : 'Update User') : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
